import { Button } from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AncillaryCostService } from "../../../../../Component/Api/merSETA/AncillaryCosts/AncillaryCostsService";
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog";
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable";
import { ColumnSortOption } from "../../../../../Component/Model/Dto/ColumnSortOption";
import { AncillaryCost } from "../../../../../Component/Model/Dto/WSP/AncillaryCost";
import { TrainingTypes } from "../../../../../Component/Model/Enum/BEE123Enums";
import { EntriesPerPage } from "../../../../../Component/Model/Table/Table";
import { defaultObject } from "../../../../../Component/Model/helpers";
import { AncillaryCostModal } from "../AncillaryCosts/AncillaryCostModal";
import { AncillaryCostsTableColumn } from "../AncillaryCosts/AncillaryCostTableColumn";
import { useRecoilValue } from "recoil";
import { InterventionAtom } from "../Intervention.Atom";

interface IProps {
    interventionType : TrainingTypes
}

export const AncillaryCostsPage = ({interventionType} : IProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showAssociateModal, setShowAssociateModal] = useState<boolean>(false);
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [columnSortOption, setColumnSortOption] = useState<ColumnSortOption>();
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [selectedAncillaryCostId, setSelectedAncillaryCostId] = useState<number>(0);
    const [selectedAncillaryCostDescription, setSelectedAncillaryCostDescription] = useState<string>('');
    const [ancillaryCosts, setAncillaryCosts] = useState<AncillaryCost[]>(defaultObject);
    const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
    const [totalItemCount, setTotalItemCount] = useState<number>(0);
    const [selectedAncillaryCost, setSelectedAncillaryCost] = useState<AncillaryCost>();
    const selectedIntervention = useRecoilValue(InterventionAtom);

    useEffect(() => {
        fetchData();
    }, [pageNumber, entriesPerPage, columnSortOption]);

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const result = await AncillaryCostService.GetAllAncillaryCosts(selectedIntervention.id, interventionType, columnSortOption, pageNumber, entriesPerPage.defaultValue);
            setAncillaryCosts(result.data.results);
            setFilteredItemCount(result.data.filteredItemsCount);
            setTotalItemCount(result.data.totalItemsCount);
        }
        catch (error) {
            console.log(error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const openAssociateAttendeeModal = (ancillaryCost?: AncillaryCost) => {
        setSelectedAncillaryCost(ancillaryCost);
        setShowAssociateModal(true);
        setShowModal(true);
    };

    const openCostsModal = (ancillaryCost?: AncillaryCost) => {
        setShowModal(true);
        setShowAssociateModal(false);
        setSelectedAncillaryCost(ancillaryCost);
    };

    const closeCostsModal = () => {
        setShowModal(false);
        setSelectedAncillaryCostId(0);
        fetchData();
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    };

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    };

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOption(sortOption)
        setPageNumber(1);
    };

    const handleDelete = (id: number, description: string) => {
        setSelectedAncillaryCostDescription(description);
        setSelectedAncillaryCostId(id);
        setShowConfirmationDialog(true);
    };

    const onAcceptDelete = async () => {
        try {
            await AncillaryCostService.Delete(selectedAncillaryCostId, selectedIntervention.id, interventionType);
            toast.success('Ancillary Cost successfully deleted');
        } catch (error) {
            console.error(error);
        } finally {
            setShowConfirmationDialog(false);
            setSelectedAncillaryCostId(0);
            fetchData();
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmationDialog(false);
    };

    const tableData = AncillaryCostsTableColumn(ancillaryCosts, openCostsModal, handleDelete , openAssociateAttendeeModal);

    return (
        <>
            <Button color="primary" variant="contained" onClick={() => {openCostsModal()}} >CAPTURE ANCILLARY COST</Button>
            <div style={{ marginTop: '16px' }}>
                <DataTable
                    key={totalItemCount}
                    table={tableData}
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemCount}
                    totalItemsCount={totalItemCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    isLoading={isLoading}
                    manualSorting={true}
                    columnSortingChanged={columnSortingChanged}
                />
            </div>
            {showModal && <AncillaryCostModal open={showModal} closeModal={closeCostsModal} ancillaryCost={selectedAncillaryCost!} interventionTypeId={interventionType} selectedInterventionId={selectedIntervention.id} openAssociateAttendee = {showAssociateModal}  />}
            <ConfirmationDialog
                isLoading={isLoading}
                showDialog={showConfirmationDialog}
                handleAccept={onAcceptDelete}
                handleCancel={handleCancelDelete}
                heading={"Confirm Delete"}
                message={`Are you sure you want to delete the Ancillary Cost: ${selectedAncillaryCostDescription}?`}
            />
        </>
    )
}